import { Template } from '../../../mocks/websiteTemplates';

interface FeaturedTemplatesProps {
  allTemplates: Template[];
  onPreview: (template: Template) => void;
}

const FEATURED_CONFIG = [
  {
    name: 'Modern Fashion Store',
    gradient: 'from-rose-600/80 via-pink-600/60 to-orange-500/40',
    icon: 'ri-shopping-bag-3-line',
  },
  {
    name: 'AI Platform Showcase',
    gradient: 'from-violet-700/80 via-indigo-600/60 to-cyan-500/40',
    icon: 'ri-cpu-line',
  },
  {
    name: 'Photographer Portfolio',
    gradient: 'from-slate-800/80 via-slate-700/60 to-gray-600/40',
    icon: 'ri-camera-lens-line',
  },
  {
    name: 'Fine Dining Restaurant',
    gradient: 'from-amber-700/80 via-orange-500/60 to-red-500/40',
    icon: 'ri-restaurant-line',
  },
];

interface SpotlightCardProps {
  template: Template;
  gradient: string;
  icon: string;
  large?: boolean;
  onPreview: (t: Template) => void;
}

function SpotlightCard({ template, gradient, icon, large, onPreview }: SpotlightCardProps) {
  return (
    <div
      className="relative group rounded-2xl overflow-hidden cursor-pointer"
      style={{ minHeight: large ? 420 : 196 }}
      onClick={() => onPreview(template)}
    >
      <img
        src={template.image}
        alt={template.name}
        className="absolute inset-0 w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
      />
      <div className={`absolute inset-0 bg-gradient-to-t ${gradient}`}></div>
      <div className="absolute inset-0 bg-black/25"></div>

      {template.badge && (
        <div className={`absolute top-4 left-4 px-2.5 py-1 text-white text-xs font-semibold rounded-full z-10 backdrop-blur-sm ${
          template.badge === 'Popular' ? 'bg-amber-500/90' : 'bg-emerald-500/90'
        }`}>
          {template.badge === 'Popular'
            ? <><i className="ri-fire-line mr-1"></i>Popular</>
            : <><i className="ri-sparkle-line mr-1"></i>New</>
          }
        </div>
      )}

      <div className="absolute inset-0 flex flex-col justify-end p-5 z-10">
        <div className={`flex items-center justify-center rounded-xl bg-white/20 backdrop-blur-sm mb-3 ${large ? 'w-11 h-11' : 'w-8 h-8'}`}>
          <i className={`${icon} text-white ${large ? 'text-lg' : 'text-sm'}`}></i>
        </div>
        <span className="text-white/70 text-xs font-medium mb-1">{template.category}</span>
        <h3 className={`text-white font-bold leading-tight mb-2 ${large ? 'text-2xl' : 'text-base'}`}>
          {template.name}
        </h3>
        {large && (
          <p className="text-white/80 text-sm leading-relaxed mb-4 line-clamp-2">
            {template.description}
          </p>
        )}
        <div className="flex items-center gap-2">
          <div className="flex flex-wrap gap-1">
            {template.tags.slice(0, large ? 3 : 2).map((tag) => (
              <span key={tag} className="px-2 py-0.5 bg-white/20 backdrop-blur-sm text-white text-xs rounded-full whitespace-nowrap">
                {tag}
              </span>
            ))}
          </div>
          <button
            onClick={(e) => { e.stopPropagation(); onPreview(template); }}
            className={`ml-auto flex-shrink-0 flex items-center gap-1.5 bg-white text-gray-900 font-semibold rounded-full hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer ${large ? 'text-sm px-4 py-2' : 'text-xs px-3 py-1.5'}`}
          >
            <i className="ri-eye-line text-xs"></i>
            Preview
          </button>
        </div>
      </div>
    </div>
  );
}

export default function FeaturedTemplates({ allTemplates, onPreview }: FeaturedTemplatesProps) {
  const featured = FEATURED_CONFIG.map((cfg) => ({
    ...cfg,
    template: allTemplates.find((t) => t.name === cfg.name),
  })).filter((item) => !!item.template) as Array<{
    name: string;
    gradient: string;
    icon: string;
    template: Template;
  }>;

  if (featured.length < 2) return null;

  const [first, second, third, fourth] = featured;

  return (
    <section className="mb-14">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <div className="w-1 h-5 rounded-full bg-gradient-to-b from-violet-500 to-cyan-500"></div>
            <span className="text-xs font-semibold text-violet-600 uppercase tracking-widest">Handpicked</span>
          </div>
          <h2 className="text-2xl font-bold text-gray-900">Featured Templates</h2>
          <p className="text-sm text-gray-500 mt-1">Top picks across every category to get you started fast</p>
        </div>
        <span className="text-sm text-gray-400 hidden sm:block">4 curated templates</span>
      </div>

      {/* Spotlight grid: 1 large left + 3 right */}
      <div className="grid grid-cols-3 gap-4" style={{ gridTemplateRows: 'auto auto' }}>
        {/* Large hero card — spans 2 rows */}
        <div className="col-span-2 row-span-2" style={{ minHeight: 420 }}>
          {first && (
            <SpotlightCard
              template={first.template}
              gradient={first.gradient}
              icon={first.icon}
              large
              onPreview={onPreview}
            />
          )}
        </div>

        {/* 3 smaller cards stacked on right */}
        {[second, third, fourth].filter(Boolean).map((item) => item && (
          <div key={item.name} style={{ height: 128 }}>
            <SpotlightCard
              template={item.template}
              gradient={item.gradient}
              icon={item.icon}
              onPreview={onPreview}
            />
          </div>
        ))}
      </div>
    </section>
  );
}
